/**
 * requestDedupe — collapses identical concurrent GETs into one network call.
 *
 * Why: on dashboard mount ~10 components each fetch org/me, roles, assets, bookings…
 * (45 API calls, 5× duplicates). Sharing the in-flight promise (plus a 1.5 s memo)
 * removes the duplicates without changing any call-site.
 *
 * Safety: only GETs are deduped; any non-GET request through the same instance
 * clears the memo, so a refetch after a mutation is always fresh.
 */
import axios from 'axios';

const MEMO_TTL_MS = 1500;

export function attachRequestDedupe(instance, { ttlMs = MEMO_TTL_MS } = {}) {
  const baseAdapter = axios.getAdapter(instance.defaults.adapter || axios.defaults.adapter);
  const inflight = new Map();   // key → Promise<response>
  const memo = new Map();       // key → { at, response }

  const keyFor = (config) => {
    const url = axios.getUri(config);
    const auth = (config.headers && (config.headers.Authorization || config.headers.authorization)) || '';
    return `${url}|${typeof auth === 'string' ? auth.slice(-24) : ''}`;
  };

  instance.defaults.adapter = async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' || config.noDedupe) {
      if (method !== 'get') memo.clear();
      return baseAdapter(config);
    }
    const key = keyFor(config);
    const hit = memo.get(key);
    if (hit && Date.now() - hit.at < ttlMs) {
      return { ...hit.response, config };
    }
    if (inflight.has(key)) {
      const shared = await inflight.get(key);
      return { ...shared, config };
    }
    const p = baseAdapter(config)
      .then((response) => {
        memo.set(key, { at: Date.now(), response });
        return response;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, p);
    return p;
  };

  return instance;
}
